import {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react"
import { OverlayLayout } from "./OverlayLayout"
import { StackedLayout } from "./StackedLayout"
import { SKIP_BACK_SECONDS, SKIP_FORWARD_SECONDS } from "./utils"
import type {
  AudioPlayerControlsProps,
  AudioPlayerControlsRef,
  PlayerRenderProps,
} from "./types"

export const AudioPlayerControls = forwardRef<
  AudioPlayerControlsRef,
  AudioPlayerControlsProps
>(function AudioPlayerControls(
  {
    src,
    initialTime = 0,
    onPlay,
    onPause,
    onEnded,
    onError,
    onSeeking,
    showSkipButtons = true,
    variant = "overlay",
  },
  ref,
) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [currentTime, setCurrentTime] = useState(initialTime)
  const [duration, setDuration] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  // Store callbacks in refs so event listeners always call the latest version
  const callbacksRef = useRef({
    onPlay,
    onPause,
    onEnded,
    onError,
    onSeeking,
    initialTime,
  })
  callbacksRef.current = {
    onPlay,
    onPause,
    onEnded,
    onError,
    onSeeking,
    initialTime,
  }

  const handleTimeUpdate = () => setCurrentTime(audioRef.current!.currentTime)
  const handleDurationChange = () => setDuration(audioRef.current!.duration)
  const handleError = () =>
    callbacksRef.current.onError?.(audioRef.current!.error)

  const handleEnded = () => {
    setIsPlaying(false)
    callbacksRef.current.onEnded?.()
  }

  const handlePlay = () => {
    setIsPlaying(true)
    callbacksRef.current.onPlay?.()
  }

  const handlePause = () => {
    setIsPlaying(false)
    callbacksRef.current.onPause?.()
  }

  const handleLoadedData = () => {
    if (callbacksRef.current.initialTime > 0) {
      audioRef.current!.currentTime = callbacksRef.current.initialTime
    }
  }

  const handleSeeking = () => callbacksRef.current.onSeeking?.(true)
  const handleSeeked = () => callbacksRef.current.onSeeking?.(false)

  // Cleanup on unmount
  useEffect(() => {
    const audio = audioRef.current
    return () => {
      if (audio) {
        audio.pause()
        audio.currentTime = 0
      }
    }
  }, [])

  const play = useCallback(() => {
    audioRef.current?.play().catch((error) => {
      console.error("Audio play failed:", error)
      setIsPlaying(false)
    })
  }, [])

  const pause = useCallback(() => audioRef.current?.pause(), [])

  const togglePlayPause = useCallback(() => {
    if (audioRef.current?.paused) {
      play()
    } else {
      pause()
    }
  }, [play, pause])

  const seek = useCallback((time: number) => {
    const audio = audioRef.current
    if (audio) audio.currentTime = time
  }, [])

  const skip = useCallback((amount: number) => {
    const audio = audioRef.current
    if (audio) audio.currentTime = audio.currentTime + amount
  }, [])

  // Expose methods to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      play,
      pause,
      get currentTime() {
        return audioRef.current?.currentTime ?? 0
      },
    }),
    [play, pause],
  )

  const handleProgressInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const time = parseFloat(e.target.value)
    seek(time)
    setCurrentTime(time)
  }

  const handleProgressKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "j":
      case "ArrowLeft":
        skip(-1)
        break
      case "l":
      case "ArrowRight":
        skip(1)
        break
      case "k":
      case " ":
        e.preventDefault()
        togglePlayPause()
        break
    }
  }

  const handleSkipBack = useCallback(() => skip(-SKIP_BACK_SECONDS), [skip])
  const handleSkipForward = useCallback(
    () => skip(SKIP_FORWARD_SECONDS),
    [skip],
  )

  const renderProps: PlayerRenderProps = {
    isPlaying,
    currentTime,
    duration,
    showSkipButtons,
    togglePlayPause,
    handleSkipBack,
    handleSkipForward,
    handleProgressInputChange,
    handleProgressKeyDown,
  }

  return (
    <>
      {src && (
        /* eslint-disable-next-line jsx-a11y/media-has-caption */
        <audio
          ref={audioRef}
          src={src}
          preload="auto"
          onTimeUpdate={handleTimeUpdate}
          onDurationChange={handleDurationChange}
          onEnded={handleEnded}
          onPlay={handlePlay}
          onPause={handlePause}
          onError={handleError}
          onLoadedData={handleLoadedData}
          onSeeking={handleSeeking}
          onSeeked={handleSeeked}
        />
      )}
      {variant === "overlay" && <OverlayLayout {...renderProps} />}
      {variant === "stacked" && <StackedLayout {...renderProps} />}
    </>
  )
})
